import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import EntryListPage from "./EntryListPage";

// ── On This Day ──────────────────────────────────────────────────
function OnThisDay({ onSelect }) {
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    client.get("/entries/on-this-day")
      .then(res => setEntries(res.data))
      .catch(() => {});
  }, []);

  if (entries.length === 0) return null;

  const today = new Date();
  const label = today.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });

  // Only show root entries; use children to detect if it's a session
  const roots = entries.filter(e => !e.session_id);
  const isSession = (e) => entries.some(c => c.session_id === e.id);

  return (
    <div style={{
      marginBottom: "24px",
      padding: "16px",
      borderRadius: "12px",
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.08)",
    }}>
      <p style={{
        fontSize: "11px", opacity: 0.4, textTransform: "uppercase",
        letterSpacing: "0.08em", marginBottom: "12px"
      }}>
        🕰️ On this day — {label}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {roots.map(e => {
          const year = new Date(e.session_date).getUTCFullYear();
          const yearsAgo = today.getFullYear() - year;
          const emoji = e.medicine_type === "mushroom" ? "🌀" : "🌿";
          const session = isSession(e);
          return (
            <div
              key={e.id}
              onClick={() => onSelect(session ? `session/${e.id}` : `entry/${e.id}`)}
              style={{
                display: "flex", alignItems: "center", gap: "12px",
                cursor: "pointer", padding: "8px 10px", borderRadius: "8px",
                transition: "background 0.15s",
              }}
              onMouseEnter={ev => ev.currentTarget.style.background = "rgba(255,255,255,0.05)"}
              onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}
            >
              <span style={{ fontSize: "18px" }}>{emoji}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: "14px", fontWeight: "500", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {e.title || e.subcategory || "Untitled"}
                </p>
                <p style={{ fontSize: "12px", opacity: 0.45, margin: 0 }}>
                  {e.subcategory && `${e.subcategory} · `}{e.state}
                  {session ? " · session" : ""}
                </p>
              </div>
              <span style={{ fontSize: "12px", opacity: 0.35, whiteSpace: "nowrap" }}>
                {yearsAgo === 1 ? "1 year ago" : `${yearsAgo} years ago`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Home Page ────────────────────────────────────────────────────
function HomePage() {
  const [entryCount, setEntryCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    client.get("/entries")
      .then(res => setEntryCount(res.data.length))
      .catch((err) => {
        if (err.response?.status === 401) {
          navigate("/login");
        } else {
          setEntryCount(0);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p>Loading...</p>;

  // NEW USER — no entries yet
  if (entryCount === 0) {
    return (
      <main style={{ maxWidth: "640px", margin: "0 auto" }}>
        <h1 style={{ marginBottom: "24px" }}>Welcome to Journey Trails</h1>
        <p style={{ lineHeight: "1.8", opacity: 0.85, fontSize: "15px" }}>
          The journey opens you. Journey Trails helps you hold what comes through.
        </p>
        <p style={{ lineHeight: "1.8", opacity: 0.75, fontSize: "14px" }}>
          Beyond standard journaling, you can track cannabis sessions and mushroom journeys,
          map intended effects to what you actually experienced, check in with your chakras,
          map sensations in your body, and tag your sessions with intention.
        </p>
        <p style={{ lineHeight: "1.8", opacity: 0.75, fontSize: "14px" }}>
          This is your space to enter each journey with intention and track what actually
          shifts over time. A grounded structure for your sessions, your states, your
          body's patterns, your writing, and more.
        </p>
        <p style={{ lineHeight: "1.8", opacity: 0.75, fontSize: "14px", marginBottom: "8px" }}>
          Build real insight as you go:
        </p>
        <ul style={{ opacity: 0.75, fontSize: "14px", lineHeight: "2", paddingLeft: "20px", marginBottom: "32px" }}>
          <li>which strains and species genuinely support your intentions</li>
          <li>which times of day your body and mind respond best</li>
          <li>how your mood and nervous system move across different journeys</li>
          <li>what patterns emerge across sessions, and what shifts over time</li>
        </ul>
        <p style={{ opacity: 0.65, fontSize: "13px", marginBottom: "32px", fontStyle: "italic" }}>
          Mindful journey mapping. Free and privacy-focused.
        </p>
        <button
          onClick={() => navigate("/new")}
          style={{ fontSize: "15px", padding: "12px 28px" }}
        >
          Start Your First Entry →
        </button>
      </main>
    );
  }

  // RETURNING USER — show entry list
  return (
    <>
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: "16px"
      }}>
        <h2 style={{ margin: 0 }}>Your Journey</h2>
      </div>

      <OnThisDay onSelect={(path) => navigate(`/${path}`)} />

      <EntryListPage onSelect={(id) => navigate(`/entry/${id}`)} />
    </>
  );
}

export default HomePage;